import {Button, DataStatus, openDialog} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetDirectSaleActivityQuery,
  useGetDirectSaleUpcomingTasksQuery,
} from '@dms/api/warehouse';
import {
  ActivitySidebarSendMessage,
  LogContactDialog,
  transformTaskToActivity,
} from '@dms/features/activity-panel';
import {openCreateTaskFormDialog} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {Nullish} from 'shared/web';

import {DirectSaleActivityList} from './DirectSaleActivityList';

interface DirectSaleActivityTabProps {
  directSaleId: string;
  customerId: string | Nullish;
}

export function DirectSaleActivityTab(props: DirectSaleActivityTabProps) {
  const [canReadTasks, canCreateTasks, canCreateActivity] = usePermissions({
    permissionKeys: ['readTask', 'createTask', 'directSaleActivityCreate'],
  });
  const {
    currentData: activities,
    isFetching: isActivityFetching,
    isError: isActivityError,
  } = useGetDirectSaleActivityQuery({directSaleId: props.directSaleId});
  const {
    currentData: tasks,
    isFetching: isTasksFetching,
    isError: isTasksError,
  } = useGetDirectSaleUpcomingTasksQuery({directSaleId: props.directSaleId});

  return (
    <DataStatus
      isLoading={
        (isActivityFetching && activities === undefined) || (isTasksFetching && tasks === undefined)
      }
      isError={isActivityError || isTasksError}
    >
      <VStack spacing={4} grow={1} minHeight={0}>
        <HStack spacing={2} wrap>
          <ActivitySidebarSendMessage
            resourceId={EntityResourceIds.directSale}
            recordId={props.directSaleId}
            customerId={props.customerId}
          />
          <Button
            leftIcon="content/add_circle"
            title={i18n.t('entity.task.actions.createTask')}
            variant="link"
            size="small"
            isDisabled={!canReadTasks || !canCreateTasks}
            onClick={() =>
              openCreateTaskFormDialog({
                relatedRecordId: props.directSaleId,
                resourceId: EntityResourceIds.directSale,
                'data-testid': 'direct-sale-activity-create-task',
              })
            }
          />
          <Show when={canCreateActivity}>
            <Button
              leftIcon="communication/phone"
              title={i18n.t('entity.activityPanel.activity.logCall')}
              variant="link"
              size="small"
              onClick={() =>
                openDialog(<LogContactDialog directSaleId={props.directSaleId} />, {
                  id: 'LOG_CONTACT_DIALOG',
                  title: i18n.t('entity.activityPanel.logContact.createLog'),
                  scrollBehavior: 'outside',
                })
              }
            />
          </Show>
        </HStack>
        <Box flex={1} overflow="auto" minHeight={0}>
          <VStack spacing={4}>
            <DirectSaleActivityList
              directSaleId={props.directSaleId}
              label={i18n.t('entity.activityPanel.activity.nextSteps')}
              items={(tasks ?? []).map(transformTaskToActivity)}
              showEmpty
            />
            <Show when={Boolean(activities?.length)}>
              <DirectSaleActivityList
                directSaleId={props.directSaleId}
                label={i18n.t('entity.activityPanel.activity.pastActivities')}
                items={activities ?? []}
              />
            </Show>
          </VStack>
        </Box>
      </VStack>
    </DataStatus>
  );
}
